import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ProductCommentsSection from './ProductCommentsSection';
import PayTRTaksitTablosu from '../../../components/payment/PayTRTaksitTablosu';
import { trackViewContent } from '../../../utils/metaPixel';

const QuickViewModal = ({ product, onClose, onAddToCart }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'reviews', or 'payment'
  const [copied, setCopied] = useState(false);

  // Variant selection state (for non-bundle products)
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);

  // Bundle child variant selections: { [productId]: { color, size } }
  const [bundleSelections, setBundleSelections] = useState({});

  const isBundle = !!(product?.isBundle || product?.is_bundle);
  const bundleItems = product?.bundleItems || product?.bundle_items || [];
  const bundleDiscount = parseFloat(product?.bundleDiscountPercentage ?? product?.bundle_discount_percentage ?? 0);

  const hasVariants = product?.variants && product?.variants?.length > 0;

  // ── DISCOUNT LOGIC ──────────────────────────────────────────────────────
  // Dynamically calculate bundle price from child items (override hardcoded DB price)
  let displayPrice;
  let originalPrice;
  let hasDiscount;
  let discountPercentage;

  if (isBundle && bundleItems?.length > 0) {
    const childSum = bundleItems?.reduce((sum, item) => sum + Number(item?.price || 0), 0);
    originalPrice = childSum;
    displayPrice = bundleDiscount > 0 ? childSum * (1 - bundleDiscount / 100) : childSum;
    hasDiscount = bundleDiscount > 0;
    discountPercentage = bundleDiscount;
  } else {
    // For regular products: hasDiscount if compareAtPrice > price
    hasDiscount = !!(product?.compareAtPrice && Number(product?.compareAtPrice) > Number(product?.price));
    displayPrice = Number(product?.price || 0);
    originalPrice = hasDiscount ? product?.compareAtPrice : null;
    discountPercentage = hasDiscount
      ? Math.round((1 - Number(product?.price) / Number(product?.compareAtPrice)) * 100)
      : 0;
  }
  // ────────────────────────────────────────────────────────────────────────

  // Derive unique colors and sizes from variants
  const uniqueColors = hasVariants
    ? [...new Set(product?.variants?.map(v => v?.color).filter(Boolean))]
    : [];
  const uniqueSizes = hasVariants
    ? [...new Set(product?.variants?.map(v => v?.size).filter(Boolean))]
    : [];

  // Find the matching variant for selected color+size
  const selectedVariant = hasVariants && selectedColor && selectedSize
    ? product?.variants?.find(v => v?.color === selectedColor && v?.size === selectedSize)
    : hasVariants && selectedColor && uniqueSizes?.length === 0
    ? product?.variants?.find(v => v?.color === selectedColor)
    : hasVariants && selectedSize && uniqueColors?.length === 0
    ? product?.variants?.find(v => v?.size === selectedSize)
    : null;

  const variantStock = selectedVariant ? selectedVariant?.stock : 0;
  const isVariantOutOfStock = hasVariants && selectedVariant && variantStock === 0;
  const isVariantSelectionComplete = hasVariants
    ? (uniqueColors?.length === 0 || selectedColor) && (uniqueSizes?.length === 0 || selectedSize)
    : true;

  // Bundle: check if all child variant selections are complete
  const isBundleSelectionComplete = isBundle
    ? bundleItems?.every(childItem => {
        const childVariants = childItem?.variants || [];
        if (!childVariants?.length) return true; // no variants needed
        const childColors = [...new Set(childVariants?.map(v => v?.color).filter(Boolean))];
        const childSizes = [...new Set(childVariants?.map(v => v?.size).filter(Boolean))];
        const sel = bundleSelections?.[childItem?.productId] || {};
        const colorOk = childColors?.length === 0 || !!sel?.color;
        const sizeOk = childSizes?.length === 0 || !!sel?.size;
        return colorOk && sizeOk;
      })
    : true;

  // Reset variant selections when product changes
  useEffect(() => {
    setSelectedColor(null);
    setSelectedSize(null);
    setBundleSelections({});
  }, [product?.id]);

  // Fire ViewContent when modal opens with a valid product
  useEffect(() => {
    if (product) {
      trackViewContent(product);
    }
  }, [product?.id]);

  if (!product) return null;

  const images = product?.imageUrls?.length > 0 
    ? product?.imageUrls 
    : product?.images?.map(url => ({ url, alt: product?.title, isPrimary: false })) || [];

  const currentImage = images?.[selectedImageIndex] || { url: product?.image, alt: product?.title };

  const handleCopyLink = async () => {
    const shareUrl = window.location?.origin + window.location?.pathname + '?product=' + product?.id;
    try {
      await navigator.clipboard?.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      const el = document.createElement('textarea');
      el.value = shareUrl;
      document.body?.appendChild(el);
      el?.select();
      document.execCommand('copy');
      document.body?.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleBundleVariantSelect = (productId, field, value) => {
    setBundleSelections(prev => ({
      ...prev,
      [productId]: {
        ...(prev?.[productId] || {}),
        [field]: value
      }
    }));
  };

  const handleAddToCartClick = () => {
    if (isBundle) {
      if (!isBundleSelectionComplete) return;
      // Build enriched bundle_items with selected variants and dynamically calculated prices
      const enrichedBundleItems = bundleItems?.map(childItem => ({
        productId: childItem?.productId,
        title: childItem?.title,
        price: childItem?.price,
        selectedColor: bundleSelections?.[childItem?.productId]?.color || null,
        selectedSize: bundleSelections?.[childItem?.productId]?.size || null,
        variants: childItem?.variants || []
      }));
      const enrichedProduct = {
        ...product,
        is_bundle: true,
        bundle_items: enrichedBundleItems,
        bundle_discount_percentage: bundleDiscount,
        // Override price with dynamically calculated displayPrice
        price: displayPrice,
        original_price: originalPrice
      };
      onAddToCart?.(enrichedProduct);
    } else if (hasVariants) {
      if (!isVariantSelectionComplete) return;
      if (isVariantOutOfStock) return;
      const enrichedProduct = {
        ...product,
        selectedColor: selectedColor || undefined,
        selectedSize: selectedSize || undefined,
        variantId: selectedVariant?.id || undefined,
        stockQuantity: variantStock,
      };
      onAddToCart?.(enrichedProduct);
    } else {
      onAddToCart?.(product);
    }
    onClose();
  };

  // Determine add-to-cart button state
  const isAddToCartDisabled = isBundle
    ? !isBundleSelectionComplete
    : !product?.inStock
      || (hasVariants && !isVariantSelectionComplete)
      || (hasVariants && isVariantSelectionComplete && isVariantOutOfStock);

  const addToCartLabel = isBundle
    ? (!isBundleSelectionComplete ? 'Tüm Seçimleri Yapın' : 'Paketi Sepete Ekle')
    : !product?.inStock
    ? 'Tükendi'
    : hasVariants && !isVariantSelectionComplete
    ? 'Renk / Beden Seçin'
    : hasVariants && isVariantOutOfStock
    ? 'Tükendi' : 'Sepete Ekle';

  // Stock display for variant products
  const displayStock = hasVariants
    ? (selectedVariant ? variantStock : null)
    : product?.stockQuantity;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-card rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e?.stopPropagation()}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              {product?.brand && (
                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                  {product?.brand}
                </p>
              )}
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold text-foreground">{product?.title}</h2>
                {isBundle && (
                  <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded-full border border-orange-200">
                    📦 SET
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopyLink}
                title="Linki Kopyala"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border bg-card hover:bg-muted transition-colors text-sm text-muted-foreground hover:text-foreground"
              >
                {copied ? (
                  <>
                    <Icon name="Check" size={16} className="text-success" />
                    <span className="text-success font-medium">Kopyalandı ✓</span>
                  </>
                ) : (
                  <>
                    <Icon name="Link" size={16} />
                    <span>Linki Kopyala</span>
                  </>
                )}
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-muted rounded-lg transition-smooth"
              >
                <Icon name="X" size={24} />
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Image Gallery */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="relative aspect-square bg-muted rounded-xl overflow-hidden">
                {currentImage?.url ? (
                  <img
                    src={currentImage?.url}
                    alt={currentImage?.alt || product?.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Icon name="Package" size={64} color="var(--color-muted-foreground)" />
                  </div>
                )}
                {hasDiscount && (
                  <span className="absolute top-2 left-2 bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded shadow-md">
                    %{discountPercentage} İndirim
                  </span>
                )}
                {product?.badge && !hasDiscount && (
                  <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-error text-error-foreground text-xs font-bold uppercase">
                    {product?.badge}
                  </div>
                )}
              </div>

              {/* Thumbnail Gallery */}
              {images?.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {images?.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImageIndex === index
                          ? 'border-primary' : 'border-border hover:border-muted-foreground'
                      }`}
                    >
                      <img
                        src={img?.url}
                        alt={img?.alt || `${product?.title} - Image ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Details */}
            <div className="space-y-6">
              {/* Price */}
              <div>
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  {hasDiscount ? (
                    <>
                      <span className="line-through text-gray-400 text-lg">
                        {Number(originalPrice)?.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}₺
                      </span>
                      <span className="text-emerald-600 font-bold text-3xl">
                        {Number(displayPrice)?.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}₺
                      </span>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-sm font-bold rounded-full">
                        %{discountPercentage} İndirim
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-3xl font-bold text-foreground">
                        {Number(displayPrice)?.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}₺
                      </span>
                    </>
                  )}
                </div>
                
                {/* Stock Status */}
                <div className="flex items-center gap-2">
                  {!hasVariants && !isBundle ? (
                    product?.inStock ? (
                      <>
                        <div className="w-2 h-2 rounded-full bg-success"></div>
                        <span className="text-sm text-success font-medium">
                          {product?.stockQuantity > 10 ? 'Stokta' : `${product?.stockQuantity} adet kaldı`}
                        </span>
                      </>
                    ) : (
                      <>
                        <div className="w-2 h-2 rounded-full bg-error"></div>
                        <span className="text-sm text-error font-medium">Stokta Yok</span>
                      </>
                    )
                  ) : !isBundle && selectedVariant ? (
                    variantStock > 0 ? (
                      <>
                        <div className="w-2 h-2 rounded-full bg-success"></div>
                        <span className="text-sm text-success font-medium">
                          {variantStock > 10 ? 'Stokta' : `${variantStock} adet kaldı`}
                        </span>
                      </>
                    ) : (
                      <>
                        <div className="w-2 h-2 rounded-full bg-error"></div>
                        <span className="text-sm text-error font-medium">Bu kombinasyon tükendi</span>
                      </>
                    )
                  ) : !isBundle ? (
                    <>
                      <div className="w-2 h-2 rounded-full bg-muted-foreground"></div>
                      <span className="text-sm text-muted-foreground">Seçim yapın</span>
                    </>
                  ) : null}
                  {product?.sku && (
                    <span className="text-xs text-muted-foreground ml-4">SKU: {product?.sku}</span>
                  )}
                </div>
              </div>

              {/* ── BUNDLE VARIANT SELECTOR ── */}
              {isBundle && bundleItems?.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-orange-200">
                    <span className="text-base">📦</span>
                    <h3 className="text-sm font-bold text-gray-800">Bu Set İçindeki Ürünler</h3>
                  </div>
                  {bundleItems?.map((childItem) => {
                    const childVariants = childItem?.variants || [];
                    const childColors = [...new Set(childVariants?.map(v => v?.color).filter(Boolean))];
                    const childSizes = [...new Set(childVariants?.map(v => v?.size).filter(Boolean))];
                    const sel = bundleSelections?.[childItem?.productId] || {};
                    const discountedPrice = bundleDiscount > 0
                      ? childItem?.price * (1 - bundleDiscount / 100)
                      : childItem?.price;

                    return (
                      <div key={childItem?.productId} className="p-3 border border-orange-100 rounded-xl bg-orange-50/50 space-y-3">
                        <div className="flex items-center gap-2">
                          {childItem?.image && (
                            <img src={childItem?.image} alt={childItem?.title} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate">{childItem?.title}</p>
                            <p className="text-xs text-gray-500">
                              {bundleDiscount > 0 ? (
                                <>
                                  <span className="line-through text-gray-400">{childItem?.price?.toLocaleString('tr-TR')}₺</span>
                                  <span className="ml-1 text-green-600 font-semibold">{discountedPrice?.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}₺</span>
                                </>
                              ) : (
                                <span>{childItem?.price?.toLocaleString('tr-TR')}₺</span>
                              )}
                            </p>
                          </div>
                        </div>

                        {/* Child Color Selector */}
                        {childColors?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">
                              <span className="text-orange-600">{childItem?.title}</span> için Renk Seçin:
                              {sel?.color && <span className="ml-1 font-normal text-gray-500">— {sel?.color}</span>}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {childColors?.map(color => {
                                const colorHasStock = childVariants?.some(v => v?.color === color && v?.stock > 0);
                                const isSelected = sel?.color === color;
                                return (
                                  <button
                                    key={color}
                                    type="button"
                                    onClick={() => handleBundleVariantSelect(childItem?.productId, 'color', isSelected ? null : color)}
                                    disabled={!colorHasStock}
                                    className={`px-2.5 py-1 rounded-lg text-xs font-medium border-2 transition-all ${
                                      isSelected
                                        ? 'border-orange-500 bg-orange-500 text-white shadow-sm'
                                        : colorHasStock
                                        ? 'border-border bg-white text-foreground hover:border-orange-400 hover:bg-orange-50'
                                        : 'border-border bg-muted text-muted-foreground opacity-50 cursor-not-allowed line-through'
                                    }`}
                                  >
                                    {color}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Child Size Selector */}
                        {childSizes?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">
                              <span className="text-orange-600">{childItem?.title}</span> için Beden Seçin:
                              {sel?.size && <span className="ml-1 font-normal text-gray-500">— {sel?.size}</span>}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {childSizes?.map(size => {
                                const sizeHasStock = childVariants?.some(
                                  v => v?.size === size && v?.stock > 0 && (!sel?.color || v?.color === sel?.color)
                                );
                                const isSelected = sel?.size === size;
                                return (
                                  <button
                                    key={size}
                                    type="button"
                                    onClick={() => handleBundleVariantSelect(childItem?.productId, 'size', isSelected ? null : size)}
                                    disabled={!sizeHasStock}
                                    className={`min-w-[36px] px-2.5 py-1 rounded-lg text-xs font-semibold border-2 transition-all ${
                                      isSelected
                                        ? 'border-orange-500 bg-orange-500 text-white shadow-sm'
                                        : sizeHasStock
                                        ? 'border-border bg-white text-foreground hover:border-orange-400 hover:bg-orange-50'
                                        : 'border-border bg-muted text-muted-foreground opacity-50 cursor-not-allowed line-through'
                                    }`}
                                  >
                                    {size}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* No variants needed */}
                        {childColors?.length === 0 && childSizes?.length === 0 && (
                          <p className="text-xs text-green-600 flex items-center gap-1">
                            <Icon name="CheckCircle" size={12} />
                            Bu ürün için seçim gerekmez
                          </p>
                        )}
                      </div>
                    );
                  })}

                  {/* Bundle selection hint */}
                  {!isBundleSelectionComplete && (
                    <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      ⚠️ Sepete eklemeden önce tüm ürünler için renk/beden seçimini tamamlayın.
                    </p>
                  )}
                  {isBundleSelectionComplete && (
                    <p className="text-xs text-green-600 bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex items-center gap-1">
                      <Icon name="CheckCircle" size={12} />
                      Tüm seçimler tamamlandı! Paketi sepete ekleyebilirsiniz.
                    </p>
                  )}
                </div>
              )}

              {/* ── REGULAR VARIANT SELECTOR (non-bundle) ── */}
              {!isBundle && hasVariants && (
                <div className="space-y-4">
                  {/* Color Selector */}
                  {uniqueColors?.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-foreground mb-2">
                        Renk
                        {selectedColor && (
                          <span className="ml-2 font-normal text-muted-foreground">— {selectedColor}</span>
                        )}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {uniqueColors?.map(color => {
                          const colorHasStock = product?.variants?.some(
                            v => v?.color === color && v?.stock > 0
                          );
                          const isSelected = selectedColor === color;
                          return (
                            <button
                              key={color}
                              onClick={() => setSelectedColor(isSelected ? null : color)}
                              disabled={!colorHasStock}
                              className={`px-3 py-1.5 rounded-lg text-sm font-medium border-2 transition-all ${
                                isSelected
                                  ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                                  : colorHasStock
                                  ? 'border-border bg-card text-foreground hover:border-primary/60 hover:bg-primary/5'
                                  : 'border-border bg-muted text-muted-foreground opacity-50 cursor-not-allowed line-through'
                              }`}
                            >
                              {color}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Size Selector */}
                  {uniqueSizes?.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-foreground mb-2">
                        Beden
                        {selectedSize && (
                          <span className="ml-2 font-normal text-muted-foreground">— {selectedSize}</span>
                        )}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {uniqueSizes?.map(size => {
                          const sizeHasStock = product?.variants?.some(
                            v => v?.size === size
                              && v?.stock > 0
                              && (!selectedColor || v?.color === selectedColor)
                          );
                          const isSelected = selectedSize === size;
                          return (
                            <button
                              key={size}
                              onClick={() => setSelectedSize(isSelected ? null : size)}
                              disabled={!sizeHasStock}
                              className={`min-w-[44px] px-3 py-1.5 rounded-lg text-sm font-semibold border-2 transition-all ${
                                isSelected
                                  ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                                  : sizeHasStock
                                  ? 'border-border bg-card text-foreground hover:border-primary/60 hover:bg-primary/5'
                                  : 'border-border bg-muted text-muted-foreground opacity-50 cursor-not-allowed line-through'
                              }`}
                            >
                              {size}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Variant selection hint */}
                  {!isVariantSelectionComplete && (
                    <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      Lütfen sepete eklemeden önce {uniqueColors?.length > 0 && !selectedColor ? 'renk' : ''}{uniqueColors?.length > 0 && !selectedColor && uniqueSizes?.length > 0 && !selectedSize ? ' ve ' : ''}{uniqueSizes?.length > 0 && !selectedSize ? 'beden' : ''} seçin.
                    </p>
                  )}
                </div>
              )}

              {/* Add to Cart Button */}
              <Button
                onClick={handleAddToCartClick}
                disabled={isAddToCartDisabled}
                variant="default"
                className="w-full"
                iconName={isAddToCartDisabled ? 'AlertCircle' : isBundle ? 'Package' : 'ShoppingCart'}
                iconPosition="left"
              >
                {addToCartLabel}
              </Button>
            </div>
          </div>

          {/* Tabs for Details and Reviews */}
          <div className="mt-8 border-t border-border pt-6">
            <div className="flex gap-4 mb-6 border-b border-border">
              <button
                onClick={() => setActiveTab('details')}
                className={`pb-3 px-4 text-sm font-medium transition-colors relative ${
                  activeTab === 'details' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Ürün Detayları
                {activeTab === 'details' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
              <button
                onClick={() => setActiveTab('reviews')}
                className={`pb-3 px-4 text-sm font-medium transition-colors relative ${
                  activeTab === 'reviews' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Yorumlar
                {activeTab === 'reviews' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
              <button
                onClick={() => setActiveTab('payment')}
                className={`pb-3 px-4 text-sm font-medium transition-colors relative ${
                  activeTab === 'payment' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Ödeme Seçenekleri
                {activeTab === 'payment' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'details' ? (
              <div className="space-y-6">
                {/* Description */}
                {product?.description && (
                  <div>
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-2">
                      Ürün Açıklaması
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {product?.description}
                    </p>
                  </div>
                )}

                {/* Technical Specifications */}
                {product?.technicalSpecs && Object.keys(product?.technicalSpecs)?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-3">
                      Teknik Özellikler
                    </h3>
                    <div className="space-y-2">
                      {Object.entries(product?.technicalSpecs)?.map(([key, value]) => (
                        <div
                          key={key}
                          className="flex items-center justify-between py-2 border-b border-border last:border-0"
                        >
                          <span className="text-sm text-muted-foreground">{key}</span>
                          <span className="text-sm font-medium text-foreground">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Physical Properties */}
                {(product?.weight || product?.dimensions) && (
                  <div>
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-3">
                      Fiziksel Özellikler
                    </h3>
                    <div className="space-y-2">
                      {product?.weight && (
                        <div className="flex items-center justify-between py-2 border-b border-border">
                          <span className="text-sm text-muted-foreground">Ağırlık</span>
                          <span className="text-sm font-medium text-foreground">{product?.weight} kg</span>
                        </div>
                      )}
                      {product?.dimensions && (
                        <div className="flex items-center justify-between py-2">
                          <span className="text-sm text-muted-foreground">Boyutlar</span>
                          <span className="text-sm font-medium text-foreground">
                            {product?.dimensions?.length} x {product?.dimensions?.width} x {product?.dimensions?.height} {product?.dimensions?.unit}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : activeTab === 'reviews' ? (
              <ProductCommentsSection productId={product?.id} />
            ) : (
              <PayTRTaksitTablosu amount={product?.price} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickViewModal;